import { IfWrites } from '../../components/write-access';
import { useState } from 'react';
import { Link, Outlet, useOutletContext, useParams } from 'react-router-dom';
import { TabLink } from '../../components/ui/scroll-reset';
import { reportTypesFor, type InputExtractionResult, type SubjectRecord } from '@ni/shared';
import { axisOfWrite } from '../../api/client';
import { useExtractRetry, useRecord } from '../../api/hooks';
import { useMe, useOrg } from '../../api/app-context';
import { isNotFound, loadErrorLine } from '../../api/errors';
import {
  KNOWLEDGE_JOIN_ACTION,
  KNOWLEDGE_RENAME_ACTION,
  NOT_FOUND,
  NOT_FOUND_LINE,
  recordImportClause,
  recordSubline,
} from '../../lib/copy';
import { RecordHeader } from '../../components/record/record-header';
import { readingLines, type ReadingLine } from '../../lib/extraction-state';
import { PersonIdentity } from '../../components/record/person-identity';
import { ProfileLinks } from '../../components/record/profile-links';
import { StatusControl } from '../../components/record/status-control';
import { ErrorLine } from '../../components/ui/error-line';
import { WorkingButton } from '../../components/ui/working-button';
import { ProseModal, summaryLine } from '../../components/prose-modal/prose-modal';
import { JoinRecordModal } from '../../components/knowledge/join-modal';
import { RenameRecordModal } from '../../components/knowledge/rename-modal';
import { OverflowMenu } from '../../components/ui/overflow-menu';
import { newDossierPath } from '../reports/new';
import { Loading } from '../../components/ui/loading';
import { EmptyLine } from '../../components/ui/empty-line';
import { Button } from '../../components/ui/button';
import { StatusWord } from '../../components/ui/status-word';
import { TopBar } from '../../components/ui/top-bar';

const offersDossier = (vertical: string) =>
  reportTypesFor(vertical).some((type) => type.key === 'dossier');

/**
 * The record shell: breadcrumb, the demo's restrained header (a monogram, a
 * name, one line, and the two things that can be done to a person), and three
 * tabs.
 *
 * Still no chips, badges or status cards — with one amendment (design note
 * §1.7): the status reads on the name's line, because the control that shows it
 * is also the control that changes it. A word that does something is not
 * decoration. Everything else the record could label itself with stays off.
 *
 * Adding a note is about the person, not about a tab, so the action sits in the
 * header and what it added is confirmed directly under it.
 *
 * Data is a route but not a tab: it is the same record at the grain the
 * substrate stores it, reached from the Research tab's foot, where a power view
 * belongs rather than in the nav a person reads every day. Network is a tab —
 * it is a reading of the record anyone might want, not a power view.
 *
 * Tabs are route-addressed so a needs-action row can deep-link straight into
 * the surface that needs the action.
 */

export interface RecordContext {
  /**
   * This dossier's subject entity id, **lowercased at the `useParams` boundary
   * below** and canonical from there down: every consumer should read it from
   * `usePersonRecord` / `useOutletContext` rather than calling `useParams`
   * again, or the raw and folded spellings are back on the same page.
   */
  subjectId: string;
  record: SubjectRecord;
  /**
   * The last add's response, so a tab can show what landed without waiting for
   * the record read to come back (the record still invalidates behind it).
   */
  justAdded: InputExtractionResult | null;
}

export function usePersonRecord(): RecordContext {
  return useOutletContext<RecordContext>();
}

/**
 * **What is being read, and what could not be**, one row per note.
 *
 * It used to be one row for `extraction_failures[0]`, saying "the notes could
 * not be read into the profile" with no idea which note it meant — so a record
 * with one bad note and three good ones printed that sentence over the three
 * good ones' facts. And it used to say "Retrying…" out of the mutation's own
 * pending flag, which meant a refresh during a retry showed a plain failure.
 *
 * Both are gone. Every word here comes out of `readingLines(record)`, which is a
 * function of the payload, so the banner a refresh draws is the banner that was
 * on the screen. The one thing the browser still knows on its own is which
 * button was just pressed, and that is used for nothing except disabling that
 * button until the post settles.
 */
export function ExtractionBannerView({
  lines,
  onRetry,
  retryBusy,
  error,
}: {
  lines: ReadingLine[];
  onRetry: (inputId: string) => void;
  /** The note whose retry post is in flight, so only its button goes dead. */
  retryBusy: string | null;
  /** A real HTTP failure from the post — not a failed reading, which is a row. */
  error?: unknown;
}) {
  if (lines.length === 0) return null;

  return (
    /* **`aria-live` because these rows change under the reader** — the record
       polls while a note is being read, so "Reading the note…" becomes a failure
       row, or disappears, with nobody having touched anything. A sighted reader
       sees that; without this a screen-reader user is told nothing at all, and
       the row they were last read is the row they believe is still there.
       `polite` and not `assertive`: a reading landing is news, not an
       interruption. */
    <div aria-live="polite">
      {lines.map((line) => (
        <div
          key={line.input_id}
          /* Work under way is not a warning: a note being read is the system
             doing what it was asked, and the flag row's own green is what this
             app says that in. Amber stays what it has always been here — a thing
             that stopped and needs a person. */
          className={line.tone === 'failed' ? 'flag amber row' : 'flag row'}
        >
          <div className="label">{line.label}</div>
          <div className="detail">{line.note}</div>
          {line.detail.length > 0 ? <div className="src">{line.detail}</div> : null}
          {line.canRetry ? (
            <div className="flag-actions">
              {/* The label is `Retry` in both states on purpose. "Retrying…"
                  is a claim about the server, and the server says it: after the
                  post settles the record comes back with the note pending and
                  this row is replaced by a reading row that says so. All the
                  press does here is stop a second post going out. */}
              <WorkingButton
                label="Retry"
                workingLabel="Retry"
                tiny
                working={retryBusy === line.input_id}
                onClick={() => onRetry(line.input_id)}
              />
            </div>
          ) : null}
        </div>
      ))}
      {error ? <ErrorLine error={error} /> : null}
    </div>
  );
}

function ExtractionBanner({ record, subjectId }: { record: SubjectRecord; subjectId: string }) {
  const retry = useExtractRetry(subjectId);

  return (
    <ExtractionBannerView
      lines={readingLines(record)}
      onRetry={(inputId) => retry.mutate(inputId)}
      retryBusy={retry.isPending ? (retry.variables ?? null) : null}
      error={retry.isError ? retry.error : null}
    />
  );
}

export function PersonRecord() {
  /* **The one place a subject id enters the app from outside**, so it is the one
     place to make it canonical.

     A uuid's hex is case-insensitive and every layer below here agrees except the
     comparisons: `ParseUUIDPipe` accepts an upper-cased address, Postgres's
     `uuid` equality folds it, and what comes back are columns rendered
     lower-case. `/people/AB12…` is therefore an ordinary 200 whose reader id
     matches nothing it is put next to — the reader named as her own counterpart,
     a flagged row written about the other party.

     Folding here also keeps one person to one cache entry: `subjectId` is a
     segment of every query key below it (`qk.brief`, `qk.network`, …), so the raw
     and the folded spelling would otherwise be two dossiers in the cache, each
     polling on its own. The comparison sites fold too (`sameEntityId`) — this is
     the canonical form, not the only defence, because ids reach `factLine` from
     places that never passed through here. `NavLink` matching is
     case-insensitive by default, so the tabs still highlight on a loud URL. */
  const { subjectId: rawSubjectId = '' } = useParams();
  const subjectId = rawSubjectId.toLowerCase();
  const record = useRecord(subjectId);
  const me = useMe();
  const org = useOrg();
  const [adding, setAdding] = useState(false);
  /**
   * **Rename, from the header** (organisations-as-subjects note §1 R6, §2 chunk
   * 1). The person half of the door: of the four early merges that kept the
   * shorter spelling, one is a person, and until this there was no way to correct
   * a person's name that did not involve folding them into another record.
   *
   * It is here rather than on the knowledge panel — which offers Rename for
   * organisations and events and deliberately not for people — because a tracked
   * person has a record page, and the name belongs where it is read.
   */
  const [renaming, setRenaming] = useState(false);
  /**
   * **Join with another record, from the same header** (organisations-as-subjects
   * note §2 chunk 1, "Join by hand").
   *
   * The other end of the gesture the knowledge panel offers. A record folded
   * away is nearly always the one with no page — a bare "Clint" that came in on
   * an import — and this is the page of the person it belongs to, which is where
   * a reader is standing when they notice.
   *
   * **Nothing has to happen on success here.** This record is tracked, a tracked
   * record cannot be the loser of a fold (the merge's satellite gate refuses it)
   * and two tracked records are a refusal before the fold is attempted — so the
   * survivor is always this page, and there is nowhere to navigate. The hook
   * drops the record, network and decision scopes on settle, so the header,
   * the graph and the History behind the modal have already caught up. All that
   * is left is to say what happened.
   */
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState<string | null>(null);
  const [justAdded, setJustAdded] = useState<InputExtractionResult | null>(null);

  if (record.isPending) {
    return (
      <div className="column record-column">
        <EmptyLine>
          <Loading />
        </EmptyLine>
      </div>
    );
  }

  if (record.isError) {
    return (
      <div className="column record-column">
        <TopBar parent={{ to: '/', label: 'People' }} />
        <div className="head">
          <div className="titles">
            <div className="title">{isNotFound(record.error) ? NOT_FOUND : 'Not available'}</div>
          </div>
        </div>
        <p className="lede">
          {isNotFound(record.error) ? NOT_FOUND_LINE : loadErrorLine(record.error)}
        </p>
      </div>
    );
  }

  const subject = record.data.subject;

  return (
    <div className="column record-column">
      {/* **Every control states its own access.** Add a note is the one a
          standard member keeps; a child that leaned on a row's gate is how
          Rename and Join came to be drawn for members who are refused them. Add
          a note reads its axis off the write it opens, so tightening the route
          on the server tightens this control with it. */}
      <TopBar
        parent={{ to: '/', label: 'People' }}
        here={subject.name}
        actions={
          <>
            <IfWrites needs={axisOfWrite('createInput')}>
              <Button variant="act" onClick={() => setAdding(true)}>
                Add a note
              </Button>
            </IfWrites>
            {/* A dossier on this person for the firm to hand over
                (docs/notes/2026-09-28-shippable-dossier-spec.md), where the
                vertical offers one and the person is on an account. It keeps
                the note gate it had in the header row as well as its own. */}
            {subject.account && offersDossier(org.vertical) ? (
              <IfWrites needs={axisOfWrite('createInput')}>
                <IfWrites needs={axisOfWrite('createReport')}>
                  <Link className="btn act" to={newDossierPath(subject.id, subject.account.id)}>
                    Prepare a dossier
                  </Link>
                </IfWrites>
              </IfWrites>
            ) : null}
            {/* **The corrections, folded behind one glyph**, the way the knowledge
                panel's head folds its own. Rename and Join are corrections to
                *which record this is*, each used about once in a record's life,
                so they fold rather than sit beside the everyday work. Both are
                single-press doors — each opens a modal, which is a layer above
                the menu rather than a second menu inside it.

                Order kept: Rename first, Join last, because Join is the one with
                no undo and a reader reaching for the other should not pass it on
                the way.

                **Owner-level, like the status control.** The refusal that matters
                is the modals' own (`AccessRefusedModal`) — an entry point can
                always be added or pasted around — and hiding the glyph as well is
                the tidier half of the same answer. */}
            <IfWrites>
              <OverflowMenu>
                <button
                  type="button"
                  role="menuitem"
                  className="overflow-menu-item"
                  onClick={() => setRenaming(true)}
                >
                  {KNOWLEDGE_RENAME_ACTION}
                </button>
                {/* It says the whole phrase rather than a word — "Join" alone says
                    nothing about what is joined to what. */}
                <button
                  type="button"
                  role="menuitem"
                  className="overflow-menu-item"
                  onClick={() => setJoining(true)}
                >
                  {KNOWLEDGE_JOIN_ACTION}
                </button>
              </OverflowMenu>
            </IfWrites>
          </>
        }
      />

      <RecordHeader
        name={subject.name}
        actions={
          <IfWrites>
            <div className="head-actions">
              <StatusControl subjectId={subjectId} subject={subject} />
            </div>
          </IfWrites>
        }
        facts={
          org.is_demo ? (
            <div className="subline">
              <StatusWord vertical={org.vertical} status={subject.status} />
            </div>
          ) : null
        }
      >
        <PersonIdentity key={subjectId} record={record.data} subjectId={subjectId} />
        <ProfileLinks links={record.data.profile_links} subjectId={subjectId} />
        {/* The status left this line when it grew a control of its own. What
              is left is the record's two dates — the second appearing only once
              there is one, since a subject nobody has reclassified says nothing
              about a change that never happened — led by the one thing that
              outranks them: "This is you", when this person is the reader
              (import note §1.14). The caller's id comes from `/me`, which the
              shell has already read; the comparison is against the *record's*
              link rather than `/me`'s own `person_entity_id`, because the same
              line has to name a colleague as well as the reader. */}
        {/* The import clause is a node rather than a clause of the string,
              because it is a link and `joinClauses` joins words. It trails the
              dates for the same reason they trail "This is you": it says where
              the record came from, which is a fact of the same order as when it
              was added, and the one clause that outranks both is the one about
              the reader (import note §2.5). */}
        <div className="subline">
          {recordSubline(subject, me.user.id)}
          {subject.import ? (
            <>
              {' · '}
              <Link className="link" to={`/imports/${subject.import.id}`}>
                {recordImportClause(subject.import.at, subject.import.filename)}
              </Link>
            </>
          ) : null}
        </div>
      </RecordHeader>

      {justAdded ? <div className="src">{summaryLine(justAdded)}</div> : null}

      {/* The server's own sentence about the fold, under the header where the
          added note's line goes — it is the one place the promise about the old
          entry is ever made, and the modal that made it is gone by now. It
          stands until the reader leaves the page: nothing else on this screen
          will tell them where the other entry went. */}
      {joined ? (
        <div className="src" aria-live="polite">
          {joined}
        </div>
      ) : null}

      <IfWrites>
        <ExtractionBanner record={record.data} subjectId={subjectId} />
      </IfWrites>

      <div className="tabs">
        <TabLink end to={`/people/${subjectId}`}>
          Overview
        </TabLink>
        <TabLink to={`/people/${subjectId}/research`}>Research</TabLink>
        <TabLink to={`/people/${subjectId}/network`}>Network</TabLink>
      </div>

      <div className="tab-panel">
        <Outlet context={{ subjectId, record: record.data, justAdded } satisfies RecordContext} />
      </div>

      {adding ? (
        <ProseModal
          subjectId={subjectId}
          onClose={() => setAdding(false)}
          onAdded={(result) => setJustAdded(result)}
        />
      ) : null}

      {/* The same modal the knowledge panel opens — one door, one endpoint, one
          set of words. `useRenameKnowledge` drops the record scope on settle, so
          the header above is already reading the new name by the time the modal
          closes; there is nothing for this to do on success. */}
      {renaming ? (
        <RenameRecordModal
          here={{ entity_id: subjectId, display_name: subject.name }}
          onClose={() => setRenaming(false)}
        />
      ) : null}

      {/* The same modal the knowledge panel opens, on the same terms. `tracked`
          is `true` and not read from anything: this page exists because the
          office keeps a record of this person, which is what tracked means. */}
      {joining ? (
        <JoinRecordModal
          here={{ entity_id: subjectId, display_name: subject.name, tracked: true }}
          onClose={() => setJoining(false)}
          onJoined={(response) => setJoined(response.sentence)}
        />
      ) : null}
    </div>
  );
}
